import { lazyWithRetry } from "@outschool/ui-utils";
import { navigate, useRedirect, useRoutes } from "@patched/hookrouter";
import React, { useEffect } from "react";

import ActivityReviewModal from "../../components/ActivityReviewModal";
import useReviewActivity from "../../hooks/useReviewActivity";
import { HeroLayoutWithTabs } from "../../layout/HeroLayoutWithTabs";
import * as Routes from "../../lib/Routes";
import { useLearnerAuth } from "../../providers/LearnerAuthProvider";
import LearnerDashboardSectionFragment from "../../queries/LearnerDashboardSectionFragment";
import ScheduleNavTabs from "./ScheduleNavTabs";

const AllLearnerActivities = lazyWithRetry(
  () => import("./AllLearnerActivities")
);
const UpcomingClasses = lazyWithRetry(() => import("./UpcomingClasses"));
const LearnerCalendar = lazyWithRetry(() => import("./LearnerCalendar"));

const dashboardRoutes = {
  "/calendar": () => <LearnerCalendar />,
  "/all": () => <AllLearnerActivities />,
  "/schedule": () => <UpcomingClasses />
};

function LearnerDashboard() {
  useRedirect("/", "/all");
  useRedirect("/upcoming-classes", "/all");
  useRedirect("/past-classes", "/all");

  const currentRoute = useRoutes(dashboardRoutes);
  const { learnerAuth } = useLearnerAuth();

  const queryParams = new URLSearchParams(window.location.search);
  const reviewSection = queryParams.get("reviewsection");

  const { activity, loading } = useReviewActivity(reviewSection);

  // The useReviewActivity hook gets an activity if its most recent meeting has not been reviewed.
  // If no activity is found, we remove the query param from the URL to ensure the query isn't repeated
  // if the user reloads the page.
  if (!activity && !loading) {
    const url = new URL(window.location.href);
    url.searchParams.delete("reviewsection");
    window.history.replaceState({}, "", url.toString());
  }

  useEffect(() => {
    if (!learnerAuth) {
      OsPlatform.captureError(
        "Unauthenticated learner loaded learner dashboard"
      );
      navigate(Routes.homePath());
    }
  }, [learnerAuth]);

  return (
    <>
      {activity && reviewSection && (
        <ActivityReviewModal activity={activity} sectionUid={reviewSection} />
      )}
      <HeroLayoutWithTabs currentPath={Routes.dashboardPath()}>
        <ScheduleNavTabs />
        {currentRoute}
      </HeroLayoutWithTabs>
    </>
  );
}

export const navigateToSection = (section: $TSFixMe) => {
  if (section.activity.isClub) {
    navigate(Routes.clubPath(section.uid));
  } else {
    navigate(Routes.classroomPath(section.uid));
  }
};

LearnerDashboard.fragments = {
  section: LearnerDashboardSectionFragment
};

export default LearnerDashboard;
